import type { LobbyServerEvent } from '@danda/shared';
import type { EventHandlerContext } from './lobby-event-types';
import type { Character } from '../lobbyStore.types';
import { apiClient } from '@/lib/api';
import { useDashboardStore } from '../dashboardStore';

// In-flight character update tracking to prevent duplicate API calls
const inFlightCharacterUpdates = new Map<string, Promise<void>>();
// Track characters that received update events while an API call was in-flight
const pendingRefresh = new Set<string>();

// HP/maxHP source-of-truth note:
// `state.characters[i].session.hp` and `permanent.max_hp` are the canonical
// values for HP. `parties[].members[].currentHp/maxHp` is a denormalized
// cache the lobby PartyPanel reads from when it doesn't have the full
// Character object. Whenever the canonical values change (here on
// `lobby:character-updated`), the handler updates both stores in a single
// coalesced set() call so panels never see an intermediate frame where
// session.hp is fresh but the party cache is stale. The inverse mirror
// (party-updated → characters) lives in party-events.ts.

export function handleCharacterEvents(event: LobbyServerEvent, ctx: EventHandlerContext): boolean {
  const { get, set } = ctx;
  const state = get();

  switch (event.type) {
    case 'lobby:character-released': {
      if (state.selectedCharacterId === event.characterId) {
        set({ selectedCharacterId: null });
        // Clear main chat history when releasing the selected character
        // Server conversation will also reset, so frontend should match
        console.log('[LobbyStore] Clearing main chat after character release', {
          characterId: event.characterId,
          characterName: event.characterName,
        });
        state.clearChatHistory('main');
      }
      // Tear down every chat-room subscription referencing the released char
      // (inner-voice, DMs, private rooms). Without this, LobbyLayout still
      // computes a roomContext targeting that char, and LobbyBackground keeps
      // sending bg-gen requests the backend rejects with "Target not in lobby".
      const staleRoomIds = state.chatRooms
        .filter((r) => r.participants?.includes(event.characterId))
        .map((r) => r.id);
      staleRoomIds.forEach((id) => state.removeChatRoom(id));
      set((s) => ({
        characters: s.characters.filter((c) => c.id !== event.characterId),
      }));
      state.addNotification({
        id: `notif-${Date.now()}`,
        type: 'system',
        title: 'Character Released',
        message: `${event.characterName} has been released and is now a wandering NPC.`,
        timestamp: new Date(event.sent_at),
        isRead: false,
      });
      return true;
    }

    case 'lobby:character-updated': {
      const charId = event.characterId;
      // A `party_id` key in the payload flags a party-membership change — the
      // backend's post-training-duel nudge sends `{ party_id: null }`. Two
      // separate web surfaces show party state and NEITHER is touched by the
      // character refetch below: the dashboard card (dashboardStore, its own
      // getParty()) and the lobby PartyPanel (useLobbyStore.parties, matched by
      // memberCharacterIds). Refresh/clean both so a returning solo player
      // stops seeing the now-deleted "Training: X vs Y" party. Mobile gets the
      // equivalent via partyProvider invalidation.
      const updatesObj = event.updates as Record<string, unknown> | undefined;
      if (updatesObj && 'party_id' in updatesObj) {
        void useDashboardStore.getState().refreshParty();
        if (updatesObj.party_id === null) {
          // Character is now party-less (e.g. returned from a solo training
          // duel). Drop it from every lobby party, discard any party left
          // empty, AND clear currentPartyId if it pointed at one of those
          // emptied parties — mirroring removeParty()'s currentPartyId
          // handling. A raw parties filter alone would leave currentPartyId
          // dangling at the deleted temp duel party, and ChatPanel treats
          // Boolean(currentPartyId) as "has party" (/start-adventure would
          // then startGame() on the dead party).
          set((s) => {
            const removedPartyIds = new Set<string>();
            const parties = s.parties
              .map((p) => {
                if (!p.memberCharacterIds.includes(charId)) return p;
                const memberCharacterIds = p.memberCharacterIds.filter((id) => id !== charId);
                if (memberCharacterIds.length === 0) removedPartyIds.add(p.id);
                return {
                  ...p,
                  memberCharacterIds,
                  members: p.members?.filter((m) => m.id !== charId),
                };
              })
              .filter((p) => p.memberCharacterIds.length > 0);
            return {
              parties,
              currentPartyId:
                s.currentPartyId && removedPartyIds.has(s.currentPartyId) ? null : s.currentPartyId,
            };
          });
        }
      }
      const existingChar = state.characters.find((c) => c.id === charId);
      if (existingChar) {
        // If the event includes inline permanent data (e.g., from merchant transactions),
        // apply it directly to avoid race conditions with Firestore read-after-write.
        const inlinePermanent = (event.updates as Record<string, unknown>)?.permanent as
          | Record<string, unknown>
          | undefined;
        if (inlinePermanent) {
          const inlineSession = (event.updates as Record<string, unknown>)?.session as
            | Record<string, unknown>
            | undefined;
          // avatar_url and portrait_url live on session, not permanent.
          const avatarUrl =
            (inlineSession?.avatar_url as string | undefined) ||
            (inlineSession?.avatarUrl as string | undefined);
          const portraitUrl =
            (inlineSession?.portrait_url as string | undefined) ||
            (inlineSession?.portraitUrl as string | undefined);
          const updates: Partial<Character> = {
            name: (inlinePermanent.name as string) || existingChar.name,
            permanent: inlinePermanent as Character['permanent'],
            avatarEmoji: inlinePermanent.avatar_emoji as string | undefined,
          };
          if (avatarUrl) {
            updates.avatar = avatarUrl;
            updates.avatarUrl = avatarUrl;
          }
          if (portraitUrl) {
            updates.portraitUrl = portraitUrl;
          }
          if (inlineSession) {
            updates.session = inlineSession as Character['session'];
          }
          // Single coalesced set(): commit characters[] + parties[].members[]
          // mirror in one zustand commit. Two sequential set() calls would
          // create a brief render frame where session.hp is updated but
          // parties[].members[].currentHp is stale — exactly the SOT-drift
          // window this mirroring exists to prevent.
          const inlineHp = inlineSession?.hp as number | undefined;
          const inlineMaxHp = inlinePermanent.max_hp as number | undefined;
          set((s) => ({
            characters: s.characters.map((c) => (c.id === charId ? { ...c, ...updates } : c)),
            parties:
              inlineHp == null && inlineMaxHp == null
                ? s.parties
                : s.parties.map((party) => {
                    if (!party.members?.some((m) => m.id === charId)) return party;
                    return {
                      ...party,
                      members: party.members.map((m) =>
                        m.id === charId
                          ? {
                              ...m,
                              ...(inlineHp != null ? { currentHp: inlineHp } : {}),
                              ...(inlineMaxHp != null ? { maxHp: inlineMaxHp } : {}),
                            }
                          : m
                      ),
                    };
                  }),
          }));
          console.log('[LobbyStore] Character updated inline', {
            charId,
            source: (event.updates as Record<string, unknown>)?.source,
            gold_cp: inlinePermanent.gold_cp,
            hasSession: !!inlineSession,
          });
          return true;
        }

        // Fallback: re-fetch from API (for non-merchant updates like Tavian char_update)
        // Check if update already in-flight for this character (prevent duplicate API calls)
        if (inFlightCharacterUpdates.has(charId)) {
          // Mark for re-fetch after current request completes
          pendingRefresh.add(charId);
          return true;
        }

        const fetchCharacter = () => {
          const updatePromise = apiClient
            .getCharacter(charId)
            .then((charData) => {
              // API returns { charId, permanent, session } - extract properly
              const { permanent, session } = charData;
              // avatar_url and portrait_url live on session (not permanent).
              // Reading from permanent here would resurrect a stale Firestore
              // field on every API re-fetch — see commit ae8ad8392 rationale.
              const avatarUrl =
                (session?.avatar_url as string | undefined) ||
                (session?.avatarUrl as string | undefined);
              const avatarEmoji = permanent.avatar_emoji as string | undefined;
              const portraitUrl =
                (session?.portrait_url as string | undefined) ||
                (session?.portraitUrl as string | undefined);
              const fetchedUpdates: Partial<Character> = {
                name: permanent.name,
                permanent,
                session,
                avatarEmoji,
              };
              if (avatarUrl) {
                fetchedUpdates.avatar = avatarUrl;
                fetchedUpdates.avatarUrl = avatarUrl;
                fetchedUpdates.avatarHash =
                  (session?.avatar_hash as string | undefined) ||
                  (session?.avatarHash as string | undefined);
              }
              if (portraitUrl) {
                fetchedUpdates.portraitUrl = portraitUrl;
              }
              const fetchedHp = session?.hp as number | undefined;
              const fetchedMaxHp = permanent?.max_hp as number | undefined;
              // Single coalesced set(): commit characters[] + parties[].members[]
              // in one zustand commit so there's no intermediate frame with
              // mismatched HP between the canonical store and the cache.
              // Mirrors the inline-permanent path semantics above.
              set((s) => ({
                characters: s.characters.map((c) =>
                  c.id === charId ? { ...c, ...fetchedUpdates } : c
                ),
                parties:
                  fetchedHp == null && fetchedMaxHp == null
                    ? s.parties
                    : s.parties.map((party) => {
                        if (!party.members?.some((m) => m.id === charId)) return party;
                        return {
                          ...party,
                          members: party.members.map((m) =>
                            m.id === charId
                              ? {
                                  ...m,
                                  ...(fetchedHp != null ? { currentHp: fetchedHp } : {}),
                                  ...(fetchedMaxHp != null ? { maxHp: fetchedMaxHp } : {}),
                                }
                              : m
                          ),
                        };
                      }),
              }));
              console.log('[LobbyStore] Character updated from backend', {
                charId,
                updates: event.updates,
                session,
              });
            })
            .catch((error) => {
              console.error('[LobbyStore] Failed to refresh character data:', error);
            })
            .finally(() => {
              // Re-fetch if updates arrived while request was in-flight
              if (pendingRefresh.has(charId)) {
                pendingRefresh.delete(charId);
                // Don't delete from inFlightCharacterUpdates — fetchCharacter replaces it
                fetchCharacter();
              } else {
                inFlightCharacterUpdates.delete(charId);
              }
            });

          inFlightCharacterUpdates.set(charId, updatePromise);
        };

        fetchCharacter();
      }
      return true;
    }

    default:
      return false;
  }
}
